import { Injectable, inject } from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';

import {TimeService} from '@shared';

import {ProblemInstanceService, Visit, BaseTask} from 'planner-shared';

import {RouteTaskView} from '../route-task-view.model';
import {RosterService} from "../../../shared/services/roster/roster.service";

@Injectable({
    providedIn: 'root'
})
export class RouteTaskService {
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly timeService = inject(TimeService);
    private readonly rosterService = inject(RosterService);


    public mapToRouteTasks(visits: Visit[]): Observable<RouteTaskView[]> {
        return this.problemInstanceService.getProblemInstance().pipe(
            map(response => {
                return visits.map(visit => {
                    const routeTask = <RouteTaskView>{
                        taskId: visit.taskId,
                        originalTaskId: visit.taskId,
                        employeeId: visit.employeeId,
                        workHourId: visit.workHourId,
                        routePlannerTaskId: visit.routePlannerTaskId,
                        name: visit.locationName,
                        serviceName: visit.serviceName,
                        duration: visit.duration,
                        skillRequirement: visit.skillRequirement,
                        isSynced: visit.isSynced,
                        isHeavy: visit.isHeavy,
                        doesRequirePhysicalAppearance: visit.doesRequirePhysicalAppearance,
                        fromTime: this.timeService.convertTime(visit.estimatedStartTime, true),
                        toTime: this.timeService.convertTime(visit.estimatedEndTime, true),
                        rideSharingConfig: visit.rideSharingConfig,
                        syncedEmployeeName: "",
                        freeTimeBeforeTask: undefined
                    };

                    const minutesSlackOnTasksToUsers = response.problemInstance.officeInfo.minutesSlackOnTasksToUsers;
                    const task = response.problemInstance.tasks.find(i => i.originalTaskId === visit.taskId);

                    if (task) {
                        const patient = response.problemInstance.patients.find(i => i.patientId === task.patientId);
                        this.mapTimeWindow(routeTask, task, minutesSlackOnTasksToUsers);
                        routeTask.patientId = task.patientId;
                        routeTask.address = patient ? patient.streetAddressNameNumberAndLetter : '';
                        routeTask.requiredEmployees = response.problemInstance.taskToReqEmployees.find(
                            i => i.taskId === task.originalTaskId
                        )?.employeeIds;
                        routeTask.rosterDepartmentName = this.rosterService.getRosterNameById(task.rosterDepartmentId);
                    } else {
                        const employeeTask = response.problemInstance.emTasksNotPatients.find(
                            i => i.originalTaskId === visit.taskId
                        );
                        const officeAddress = response.problemInstance.officeInfo.streetAddressNameNumberAndLetter;
                        if (employeeTask) {

                            routeTask.isEmployeeTask = true;
                            if (employeeTask.streetAddressNameNumberAndLetter) {
                                routeTask.address = employeeTask.streetAddressNameNumberAndLetter;
                            } else {
                                routeTask.address = officeAddress;
                            }
                            this.mapTimeWindow(routeTask, employeeTask, minutesSlackOnTasksToUsers);
                            routeTask.rosterDepartmentName =
                                this.rosterService.getRosterNameById(employeeTask.rosterDepartmentId);
                            routeTask.isShiftSplitTask = employeeTask.isShiftSplitTask;
                        } else {
                            const generalTask = response.problemInstance.geTasksNotPatients.find(
                                i => i.originalTaskId === visit.taskId
                            );
                            if (generalTask) {
                                this.mapTimeWindow(routeTask, generalTask, minutesSlackOnTasksToUsers);
                                routeTask.allowedShiftCodes = generalTask.allowedShiftCodes;
                                routeTask.prohibitedShiftCodes = generalTask.prohibitedShiftCodes;
                                if (generalTask.streetAddressNameNumberAndLetter) {
                                    routeTask.address = generalTask.streetAddressNameNumberAndLetter;
                                } else {
                                    routeTask.address = officeAddress;
                                }
                                routeTask.rosterDepartmentName =
                                    this.rosterService.getRosterNameById(generalTask.rosterDepartmentId);
                            }
                        }
                    }

                    routeTask.outsideTimeWindow = routeTask.toTime > routeTask.timeWindowEndTime;
                    routeTask.travelTimeFromPrev = Math.floor(visit.travelTimeFromPrev / 60);

                    if (visit.rideSharingConfig) {
                        routeTask.address = visit.streetAddressNameNumberAndLetter;
                    }
                    return routeTask;
                });
            })
        );
    }

    public estimateRouteSyncTaskPairs(tasks: Visit[]): Visit[] {
        return tasks
            .filter(task => task.isSynced && !task.rideSharingConfig)
            .reduce((acc, task, _, arr) => {
                const hasPair = arr.some(
                    otherSyncedTask => otherSyncedTask.employeeId !== task.employeeId &&
                        (otherSyncedTask.locationName === task.locationName || otherSyncedTask.taskId === task.taskId)
                );
                if (hasPair) {
                    acc.push(task);
                }
                return acc;
            }, []);
    }

    public estimateUnpairedRouteSyncTasks(tasks: Visit[]): Visit[] {
        const syncedTasks = tasks.filter(task => task.isSynced && !task.rideSharingConfig);
        if (syncedTasks.length > 0) {
            const routePairedSyncTasks =
                this.estimateRouteSyncTaskPairs(tasks);
            const syncedIds = new Set(routePairedSyncTasks.map(syncedTask => syncedTask.taskId));
            return syncedTasks.filter(task => !syncedIds.has(task.taskId));
        } else {
            return [];
        }
    }

    private mapTimeWindow<T extends BaseTask>(
        routeTask: RouteTaskView,
        task: T,
        minutesSlackOnTasksToUsers: number
    ): void {
        const fromTime = this.timeService.convertTime(task.fromTime, true);
        const toTime = this.timeService.convertTime(task.toTime, true);

        routeTask.numberOfHigherSkillLevelsAccepted = task.numberOfHigherSkillLevelsAccepted;
        routeTask.timeWindowStartTime = this.timeService.addMinutesToTime(fromTime, -minutesSlackOnTasksToUsers);
        routeTask.timeWindowEndTime = this.timeService.addMinutesToTime(toTime, minutesSlackOnTasksToUsers);
    }
}
